'use client';

import { use, useEffect, useState } from 'react';

import { ArticleSectionRelated } from '@/app_components/ArticleSections/ArticleSectionRelated';
import { getSeenArticleIds } from '@/app_lib/services/recommendations/seenArticles';
import type { Classification } from '@/app_lib/types/Article';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import type { EventData } from '@/app_lib/types/Gtm';
import { getLatestHeading } from '@/app_lib/utils/sharedUtils';

const recommendedTrackingData: EventData = {
  // biome-ignore lint/style/useNamingConvention: gtm data
  contentPlacementCD: 'recommended - bottom', //<module> - <position>
  eventAction: 'recommended module click',
  eventCategory: 'navigation',
};

const latestTrackingData: EventData = {
  // biome-ignore lint/style/useNamingConvention: gtm data
  contentPlacementCD: 'latest - bottom',
  eventAction: 'latest module click',
  eventCategory: 'navigation',
};

interface RecommendedArticlesProps {
  currentArticleId: string;
  fallbackPromise: Promise<ArticlePreview[]>;
  recommendationsPromise: Promise<ArticlePreview[] | null>;
  section: Classification['primarySection'];
}

export const RecommendedArticles = ({
  currentArticleId,
  fallbackPromise,
  recommendationsPromise,
  section,
}: RecommendedArticlesProps) => {
  const serverRecommendations = use(recommendationsPromise);
  const fallbackArticles = use(fallbackPromise);
  const [clientRecommendations, setClientRecommendations] = useState<ArticlePreview[] | null>(null);

  useEffect(() => {
    const seenIds = getSeenArticleIds();
    if (seenIds.length === 0) {
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      context: 'article',
      currentArticleId,
      limit: '6',
    });
    for (const id of seenIds) {
      params.append('excludeIds', id);
    }

    const fetchRecommendations = async () => {
      try {
        const res = await fetch(`/api/recommendations?${params}`, { signal: controller.signal });
        if (!res.ok) {
          return;
        }
        const data: ArticlePreview[] = await res.json();
        if (data?.length > 0) {
          setClientRecommendations(data);
        }
      } catch {
        // AbortError or network failure; keep showing server-streamed data
      }
    };

    fetchRecommendations();

    return () => controller.abort();
  }, [currentArticleId]);

  const recommendations = clientRecommendations ?? serverRecommendations;
  const articles = recommendations ?? fallbackArticles;

  if (!articles.length) {
    return null;
  }

  return (
    <ArticleSectionRelated
      articles={articles}
      heading={recommendations ? 'Recommended For You' : getLatestHeading(section)}
      moduleType={recommendations ? 'recommended' : 'latest'}
      trackingData={recommendations ? recommendedTrackingData : latestTrackingData}
    />
  );
};
